import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://theyemberzal.com';
const DEFAULT_IMAGE = `${SITE_URL}/favlogo.png`;
const SITE_NAME = 'Yemberzal';

/**
 * Reusable SEO component for dynamic per-page meta tags.
 *
 * @param {string} title        – Page title (appended with " | Yemberzal")
 * @param {string} description  – Meta description (max ~155 chars ideal)
 * @param {string} path         – URL path, e.g. "/shop" (canonical will be SITE_URL + path)
 * @param {string} image        – Open Graph image URL (absolute)
 * @param {string} type         – og:type, default "website"
 * @param {object} jsonLd       – Optional JSON-LD structured data object
 * @param {string} noindex      – If true, adds noindex
 */
export default function SEO({
  title,
  description,
  path = '',
  image,
  type = 'website',
  jsonLd,
  noindex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Kashmiri Fashion Discovery & Virtual Try-On Platform`;
  const metaDescription =
    description ||
    'Discover and shop authentic Kashmiri fashion online. Browse pashmina shawls, phiran, aari embroidery, and handcrafted Koshur clothing from 50+ Kashmiri brands.';
  const canonical = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonical} />

      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
